import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banknote, CircleCheck, Search, Zap, type LucideIcon } from 'lucide-react';
import { listDomains } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { Mark } from '../../components/brand/Logo';

/** One stop on the tour. `route` navigates there first; `anchor` is scrolled into
 * view and spotlighted; omit both for a calm centered card. */
interface Step {
  icon?: LucideIcon;
  title: string;
  body: string;
  route?: (envId: string) => string;
  anchor?: string;
}

const STORAGE_KEY = 'lizrd.tour.v2';

const STEPS: Step[] = [
  {
    title: 'Welcome — let’s take the scenic route',
    body: 'A quick, five-stop walk through how Lizrd turns cloud spend into realized savings. Take your time — use Next when you’re ready, and Skip whenever you like.',
  },
  {
    icon: Banknote,
    title: 'Start with the savings pipeline',
    body: 'This is your cost-optimization status: what we’ve found, what’s in progress, and what’s been validated — with the money already banked. It’s the heartbeat of the whole loop.',
    route: () => '/',
    anchor: '[data-tour="cost-status"]',
  },
  {
    icon: CircleCheck,
    title: 'This is where the work happens',
    body: 'The optimization board — every opportunity, prioritized by value vs. effort, each with an owner and a dollar figure. This demo has around $13K/month sitting here, ready to act on.',
    route: (env) => `/domains/${env}/cost-optimization`,
    anchor: '[data-tour="cost-board"]',
  },
  {
    icon: Search,
    title: 'And this is the map underneath it',
    body: 'The Explorer: every resource you run and what it costs, tied back to the code that owns it. This is how we know where the waste is — total transparency, no spreadsheets.',
    route: (env) => `/domains/${env}/explorer`,
    anchor: '[data-tour="explorer"]',
  },
  {
    icon: Zap,
    title: 'That’s the loop — now make it yours',
    body: 'See it → act on it → prove the savings, and we even write the exact change for you. Poke around freely; you can reset the demo anytime from the banner up top.',
    route: () => '/',
  },
];

type Rect = { top: number; left: number; width: number; height: number };
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Poll for an element to mount (after a route change), up to `timeout` ms. */
async function waitForEl(
  selector: string,
  timeout: number,
  cancelled: () => boolean,
): Promise<Element | null> {
  const deadline = Date.now() + timeout;
  while (Date.now() < deadline) {
    if (cancelled()) return null;
    const el = document.querySelector(selector);
    if (el) return el;
    await sleep(100);
  }
  return document.querySelector(selector);
}

/**
 * Guided tour (Feature 24, Phase C — reworked). Auto-starts once per browser for a
 * sandbox visitor and walks — at a calm pace — to the parts that matter, actually
 * navigating between pages and scrolling each target into view before spotlighting
 * it. Degrades gracefully to a centered card if a target isn't found.
 */
export function GuidedTour() {
  const user = useAuthStore((s) => s.user);
  const isSandbox = user?.organizationKind === 'SANDBOX';
  const navigate = useNavigate();
  const domains = useQuery({ queryKey: ['domains'], queryFn: listDomains });
  const envId = domains.data?.[0]?.id;

  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  // Auto-start once per browser for a sandbox visitor.
  useEffect(() => {
    if (!isSandbox) return;
    let done = false;
    try {
      done = localStorage.getItem(STORAGE_KEY) === 'done';
    } catch {
      /* private mode — just show it */
    }
    if (!done) setActive(true);
  }, [isSandbox]);

  const step = STEPS[i];
  const needsEnv = !!step?.route && step.route.length > 0;

  // Drive each step: navigate → wait for the anchor → scroll it into view → measure.
  useEffect(() => {
    if (!active || !step) return;
    if (needsEnv && !envId) return; // wait until we know the domain
    let cancelled = false;
    setRect(null);

    const measure = () => {
      if (!step.anchor) return;
      const el = document.querySelector(step.anchor);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    void (async () => {
      const target = step.route ? step.route(envId ?? '') : null;
      if (target && window.location.pathname !== target) {
        navigate(target);
        await sleep(120); // let the route swap begin
      }
      if (!step.anchor) return; // centered card
      const el = await waitForEl(step.anchor, 2500, () => cancelled);
      if (cancelled || !el) return; // graceful centered fallback
      // Scroll the BEGINNING of the anchor to the top of the viewport. For the
      // tall board/overview wrappers (steps 3 & 4), `block: 'center'` tried to
      // center a taller-than-viewport element, which scrolled past the bottom of
      // the board and left the coach-mark card off-screen above. 'start' keeps us
      // at the top of the section, where the card is placed and visible.
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      await sleep(420); // let the smooth scroll settle
      if (!cancelled) measure();
    })();

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, i, envId]);

  if (!active || !step) return null;

  const finish = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'done');
    } catch {
      /* ignore */
    }
    setActive(false);
  };
  const next = () => (i + 1 < STEPS.length ? setI(i + 1) : finish());
  const back = () => setI(Math.max(0, i - 1));

  const pad = 10;
  const cardW = 380;
  const cardH = 210; // approximate card height for placement math
  const gap = 14;
  // Choose a card `top` that is always on-screen. Prefer just below the whole
  // anchor (small targets like step 2); if the anchor is taller than the viewport
  // (the boards in steps 3 & 4, now scrolled to the top), that doesn't fit, so
  // pin the card near the top of the section — then clamp to the viewport so it
  // can never end up above the fold.
  const belowAnchor = rect ? rect.top + rect.height + gap : 0;
  const aboveAnchor = rect ? rect.top - gap - cardH : 0;
  const rawTop = rect
    ? belowAnchor + cardH < window.innerHeight
      ? belowAnchor
      : aboveAnchor > gap
        ? aboveAnchor
        : Math.max(gap, rect.top + gap) // tall anchor → sit at the top of the board
    : 0;
  const cardTop = Math.min(Math.max(gap, rawTop), Math.max(gap, window.innerHeight - cardH - gap));
  const cardStyle: React.CSSProperties = rect
    ? {
        top: cardTop,
        left: Math.min(Math.max(16, rect.left), Math.max(16, window.innerWidth - cardW - 16)),
        width: cardW,
      }
    : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: cardW };

  return (
    <div
      className="fixed inset-0 z-[100]"
      role="dialog"
      aria-modal="true"
      aria-label="Product tour"
    >
      {/* Spotlight: a hole over the anchor via a huge box-shadow; full dim otherwise. */}
      {rect ? (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-iris transition-all duration-300"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            boxShadow: '0 0 0 9999px color-mix(in srgb, var(--obsidian) 78%, transparent)',
          }}
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: 'color-mix(in srgb, var(--obsidian) 78%, transparent)' }}
        />
      )}

      {/* Coach-mark card */}
      <div
        className="absolute rounded-xl border border-hairline bg-surface p-6 shadow-2xl transition-all duration-300"
        style={cardStyle}
      >
        <div className="flex items-start gap-3">
          {(() => {
            const StepIcon = step.icon;
            return StepIcon ? (
              <StepIcon aria-hidden className="h-7 w-7 shrink-0 text-iris" />
            ) : (
              <Mark size={30} className="shrink-0" />
            );
          })()}
          <div className="min-w-0">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
              Step {i + 1} of {STEPS.length}
            </p>
            <h3 className="mt-0.5 font-display text-base font-bold text-bone">{step.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-haze">{step.body}</p>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between">
          <div className="flex gap-1.5" aria-hidden>
            {STEPS.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === i ? 'w-4 bg-iris' : 'w-1.5 bg-border'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={finish}
              className="px-2 py-1 text-xs text-muted transition-colors hover:text-bone"
            >
              Skip
            </button>
            {i > 0 && (
              <button
                type="button"
                onClick={back}
                className="rounded-md border border-border px-3 py-1.5 text-xs text-bone transition-colors hover:bg-surface-2"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="rounded-md bg-iris px-3.5 py-1.5 text-xs font-medium text-on-iris transition-colors hover:bg-iris-strong"
            >
              {i + 1 < STEPS.length ? 'Next' : 'Explore →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
